import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaJava, FaReact } from "react-icons/fa";
import {
  SiSpringboot,
  SiMysql,
  SiTailwindcss,
  SiApachekafka,
  SiGit,
  SiGithub,
  SiSonarqube,
  SiDynatrace,
  SiJunit5,
  SiKibana,
  SiJenkins,
  SiIntellijidea,
  SiMicrostrategy,
} from "react-icons/si";
import { DiVisualstudio, DiEclipse } from "react-icons/di";
import { CheckCircle2 } from "lucide-react";

const skillCategories = {
  Languages: [
    {
      name: "Java",
      level: "Advanced",
      icon: <FaJava size={34} />,
    },
    {
      name: "MySQL",
      level: "Advanced",
      icon: <SiMysql size={34} />,
    },
  ],

  Frameworks: [
    {
      name: "Spring Boot",
      level: "Advanced",
      icon: <SiSpringboot size={34} />,
    },
    {
      name: "Microservices",
      level: "Advanced",
      icon: <SiMicrostrategy size={34} />,
    },
    {
      name: "React",
      level: "Intermediate",
      icon: <FaReact size={34} />,
    },
    {
      name: "Tailwind CSS",
      level: "Intermediate",
      icon: <SiTailwindcss size={34} />,
    },
  ],

  DevOps: [
    {
      name: "Kafka",
      level: "Intermediate",
      icon: <SiApachekafka size={34} />,
    },
    {
      name: "Jenkins",
      level: "Intermediate",
      icon: <SiJenkins size={34} />,
    },
    {
      name: "SonarQube",
      level: "Intermediate",
      icon: <SiSonarqube size={34} />,
    },
    {
      name: "Dynatrace",
      level: "Intermediate",
      icon: <SiDynatrace size={34} />,
    },
    {
      name: "Kibana",
      level: "Intermediate",
      icon: <SiKibana size={34} />,
    },
  ],

  Tools: [
    {
      name: "Git",
      level: "Advanced",
      icon: <SiGit size={34} />,
    },
    {
      name: "GitHub",
      level: "Advanced",
      icon: <SiGithub size={34} />,
    },
    {
      name: "JUnit 5",
      level: "Advanced",
      icon: <SiJunit5 size={34} />,
    },
    {
      name: "IntelliJ IDEA",
      level: "Advanced",
      icon: <SiIntellijidea size={34} />,
    },
    {
      name: "VS Code",
      level: "Advanced",
      icon: <DiVisualstudio size={34} />,
    },
    {
      name: "Eclipse",
      level: "Advanced",
      icon: <DiEclipse size={34} />,
    },
  ],
};

const Skills = () => {
  const [activeTab, setActiveTab] = useState("Languages");

  return (
    <section
      id="skills"
      className="relative overflow-hidden bg-[#08080c] px-6 py-28 text-white sm:px-10 lg:px-20"
    >
      <div className="absolute left-1/2 top-0 h-96 w-96 -translate-x-1/2 rounded-full bg-orange-500/[0.035] blur-[120px]" />

      <div className="relative z-10 mx-auto max-w-6xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center"
        >
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.3em] text-orange-400">
            Technical Expertise
          </p>

          <h2 className="text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl">
            My <span className="text-orange-500">stack.</span>
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-7 text-gray-500">
            Technologies and tools I use to design, build, test and maintain
            modern applications.
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="mt-12 flex flex-wrap justify-center gap-2">
          {Object.keys(skillCategories).map((category) => {
            const active = activeTab === category;

            return (
              <button
                key={category}
                onClick={() => setActiveTab(category)}
                className={`rounded-xl px-5 py-2.5 text-sm font-medium transition-all duration-300 ${
                  active
                    ? "bg-orange-500 text-white shadow-lg shadow-orange-500/20"
                    : "border border-white/10 bg-white/[0.025] text-gray-500 hover:border-orange-500/20 hover:text-gray-200"
                }`}
              >
                {category}
              </button>
            );
          })}
        </div>

        {/* Skills */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{
              opacity: 0,
              y: 15,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            exit={{
              opacity: 0,
              y: -15,
            }}
            transition={{
              duration: 0.3,
            }}
            className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4"
          >
            {skillCategories[activeTab].map((skill, index) => (
              <motion.div
                key={skill.name}
                initial={{
                  opacity: 0,
                  scale: 0.95,
                }}
                animate={{
                  opacity: 1,
                  scale: 1,
                }}
                transition={{
                  delay: index * 0.06,
                }}
                whileHover={{
                  y: -5,
                }}
                className="group rounded-2xl border border-white/10 bg-white/[0.025] p-5 transition-all duration-300 hover:border-orange-500/25 hover:bg-orange-500/[0.035]"
              >
                <div className="flex items-start justify-between">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-black/20 text-gray-300 transition group-hover:border-orange-500/20 group-hover:text-orange-400">
                    {skill.icon}
                  </div>

                  <CheckCircle2 size={15} className="text-emerald-500/50" />
                </div>

                <h3 className="mt-5 font-semibold text-gray-200">
                  {skill.name}
                </h3>

                <p className="mt-1 text-xs text-gray-600">{skill.level}</p>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        {/* Bottom */}
        <div className="mt-12 grid gap-3 sm:grid-cols-3">
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <p className="text-xs uppercase tracking-widest text-gray-600">
              Backend
            </p>

            <p className="mt-2 text-sm font-semibold text-gray-300">
              Java • Spring Boot • JPA • Hibernate
            </p>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <p className="text-xs uppercase tracking-widest text-gray-600">
              Architecture
            </p>

            <p className="mt-2 text-sm font-semibold text-gray-300">
              REST APIs • Microservices • JWT
            </p>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <p className="text-xs uppercase tracking-widest text-gray-600">
              Quality
            </p>

            <p className="mt-2 text-sm font-semibold text-gray-300">
              JUnit • SonarQube • Monitoring
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Skills;
